// FIXME: Some ts/eslint setting is causing the parameter names to be incorrectly marked as unused-vars
/* eslint-disable no-unused-vars */
type SetTimeoutFunc<T> = (handler: () => void, delay: number) => T;
type ClearTimeoutFunc<T> = (cancellationToken: T) => void;
/* eslint-enable no-unused-vars */

export type ShowLoaderFunc = (
  /* eslint-disable no-unused-vars */
  messsage: string | null,
  action: 'show' | 'hide',
  distanceFromTop?: number,
  /* eslint-enable no-unused-vars */
) => void;

interface ShowArgs {
  message: string;
  delay?: boolean;
  distanceFromTop?: number;
  promise?: Promise<void>;
}

export class Loader<T> {
  showLoader: ShowLoaderFunc;

  setTimeout: SetTimeoutFunc<T>;

  clearTimeout: ClearTimeoutFunc<T>;

  isSuppressedByPromise: boolean;

  loaderPromise?: Promise<void> | undefined;

  isSuppressingOnce: boolean;

  isLoaderShown: boolean;

  loaderTimeout: T | null;

  constructor(
    showLoader: ShowLoaderFunc,
    setTimeout: SetTimeoutFunc<T>,
    clearTimeout: ClearTimeoutFunc<T>,
  ) {
    this.showLoader = showLoader;
    this.setTimeout = setTimeout;
    this.clearTimeout = clearTimeout;
    this.loaderPromise = undefined;
    this.isSuppressedByPromise = false;
    this.isSuppressingOnce = false;
    this.isLoaderShown = false;
    this.loaderTimeout = null;
  }

  /**
   * Hide the loader if it's visible.
   */
  hide(): void {
    if (!this.isLoaderShown || this.loaderPromise !== undefined) {
      return;
    }

    this.isLoaderShown = false;

    this.showLoader(null, 'hide');

    if (this.loaderTimeout !== null) {
      this.clearTimeout(this.loaderTimeout);
    }
  }

  /**
   * Show the loader.
   *
   * @param message Message to show.
   * @param delay [optional] Delay until the loader starts to show (boolean).
   * @param distanceFromTop [optional] Distance from the top [px].
   */
  show({ message, delay, distanceFromTop, promise }: ShowArgs): void {
    if (this.isSuppressedByPromise || this.isSuppressingOnce) {
      this.isSuppressingOnce = false;
      return;
    }

    if (this.isLoaderShown) return;

    this.isLoaderShown = true;

    this.loaderTimeout = delay
      ? this.setTimeout(() => {
          this.showLoader(message, 'show', distanceFromTop);
        }, 1000)
      : this.loaderTimeout;

    if (!delay) {
      this.showLoader(message, 'show', distanceFromTop);
    }

    // if we get the promise as an argument we're gonna keep the loader open
    //  until this promise resolves
    if (promise) {
      this.loaderPromise = promise;
      promise.finally(() => {
        this.loaderPromise = undefined;
        this.hide();
      });
    }
  }

  /**
   * Prevent the Loader spinner from displaying while `promise` is executing.
   * This suppression ends when `promise` completes or rejects.
   *
   * Usage example :
   *
   * var deferred = $q.defer();
   * Loader.suppress(deferred.promise);
   *
   * @param {Promise<R>} promise The function or promise to suppress the loader widget.
   * @returns {Promise<R>}
   */
  suppress<R>(promise: Promise<R>): Promise<R> {
    this.isSuppressedByPromise = true;
    return promise.finally(() => {
      this.isSuppressedByPromise = false;
    });
  }

  /**
   * Temporarily suppress the next loader being shown.
   *
   * Alternatively suppresses next to call to Loader.show(...) if executed without any parameter.
   *
   * Usage example:
   *
   * Loader.suppressOnce();
   */
  suppressOnce(): void {
    this.isSuppressingOnce = true;
  }
}
